/**
 * Plan Service
 *
 * Retrieves subscription plans from Firestore with caching.
 * Plans are immutable after creation, so caching is safe.
 */

import {
  getFirestoreClient,
  type FirestoreConstraint,
} from "../lib/firestoreClient";
import type { Plan, PricingPlan, FeatureItem, Feature } from "../types/plan";

const ACTIVE_FILTER: FirestoreConstraint = {
  type: "where",
  field: "active",
  operator: "==",
  value: true,
};

/**
 * Fetch all active plans from Firestore.
 * The SDK's persistentLocalCache handles caching.
 */
export async function getActivePlans(): Promise<Plan[]> {
  const client = getFirestoreClient();
  const result = await client.queryCollection(["plans"], [ACTIVE_FILTER]);

  const plans = result.docs.map(
    (doc) =>
      ({
        ...(doc.data() as Record<string, unknown>),
        id: doc.id,
      }) as unknown as Plan
  );

  plans.sort((a, b) => {
    const categoryOrder = { basic: 0, advanced: 1 };
    return (
      (categoryOrder[a.category as keyof typeof categoryOrder] ?? 999) -
      (categoryOrder[b.category as keyof typeof categoryOrder] ?? 999)
    );
  });

  return plans;
}

/**
 * Fetch a specific plan by ID (with version)
 * Example ID: "basic-v1"
 */
export async function getPlan(planId: string): Promise<Plan | null> {
  const plans = await getActivePlans();
  return plans.find((p) => p.id === planId) || null;
}

/**
 * Fetch all features from Firestore.
 * The SDK's persistentLocalCache handles caching.
 */
export async function getAllFeatures(): Promise<Feature[]> {
  const client = getFirestoreClient();
  const result = await client.queryCollection(["features"], []);

  const features = result.docs.map(
    (doc) =>
      ({
        ...(doc.data() as Record<string, unknown>),
        id: doc.id,
      }) as unknown as Feature
  );
  return features;
}

/**
 * Convert a Firestore plan to a pricing UI model
 * Includes all features, marking as included/excluded based on plan
 */
export async function toPricingPlan(
  sourcePlan: Plan,
  allFeatures: Feature[]
): Promise<PricingPlan> {
  const planFeatureIds = new Set(sourcePlan.features);

  // Build feature list with all features, marking included status
  const features: FeatureItem[] = allFeatures.map((feature) => ({
    id: feature.id,
    name: feature.name,
    included: planFeatureIds.has(feature.id),
  }));

  return {
    id: sourcePlan.id,
    name: sourcePlan.name,
    description: sourcePlan.description,
    category: sourcePlan.category,
    monthlyPrice: formatCents(sourcePlan.pricing.monthly.amount),
    annualPrice: formatCents(sourcePlan.pricing.annual.amount),
    annualRegularPrice: formatCents(sourcePlan.pricing.regularAnnual.amount),
    annualMonthly: formatCents(sourcePlan.pricing.monthlyEquivalent.amount),
    features,
    recommended: sourcePlan.category === "advanced",
    trial: sourcePlan.trial,
  };
}

/**
 * Format cents to currency string
 * Example: 899 → "$8.99"
 */
function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
